import React from 'react';
import { cn } from './cn';

/**
 * Contêineres do Atmos: borda, raio 12, sombra sutil.
 * Regra do sistema: card nunca é aninhado em outro card.
 */
export function Card({
  interactive,
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { interactive?: boolean }) {
  return (
    <div
      className={cn(
        'rounded-xl border border-line bg-surface shadow-xs',
        interactive &&
          'cursor-pointer transition-[box-shadow,border-color] duration-[120ms] hover:border-faint hover:shadow-md',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  description,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-4 border-b border-line-subtle px-[18px] py-4',
        className,
      )}
    >
      <div className="min-w-0">
        <h3 className="text-[15px] font-semibold tracking-[-0.015em] text-fg">{title}</h3>
        {description && <p className="mt-1 text-[13px] text-fg-2">{description}</p>}
      </div>
      {actions && <div className="flex flex-none items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn('p-[18px]', className)}>{children}</div>;
}

/** Card de indicador — rótulo, número tabular e variação opcional. */
export function StatCard({
  label,
  value,
  delta,
  icon,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Variação vs. período anterior; o sinal define a cor. */
  delta?: { value: React.ReactNode; direction: 'up' | 'down' | 'flat' };
  icon?: React.ReactNode;
  className?: string;
}) {
  const tone =
    delta?.direction === 'up'
      ? 'text-success'
      : delta?.direction === 'down'
        ? 'text-danger'
        : 'text-muted';
  return (
    <Card className={cn('p-4', className)}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-muted">{label}</p>
        {icon && <span className="flex-none text-muted">{icon}</span>}
      </div>
      <p className="mt-1.5 text-2xl font-semibold tracking-[-0.03em] tabular-nums text-fg">{value}</p>
      {delta && (
        <p className={cn('mt-1 flex items-center gap-1 text-xs', tone)}>{delta.value}</p>
      )}
    </Card>
  );
}

/** Estado vazio — ícone, uma linha e no máximo uma ação. */
export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-sunken p-8 text-center',
        className,
      )}
    >
      {icon && <span className="text-muted">{icon}</span>}
      <p className="text-[12.5px] font-semibold text-fg">{title}</p>
      {description && <p className="text-xs text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Cabeçalho de página — título, subtítulo e ações à direita. */
export function PageHeader({
  title,
  description,
  icon,
  actions,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('mb-6 flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="flex min-w-0 items-center gap-3">
        {icon && (
          <span className="grid h-11 w-11 flex-none place-items-center rounded-xl border border-primary-line bg-primary-soft text-primary">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <h1 className="text-[26px] font-semibold tracking-[-0.03em] text-fg">{title}</h1>
          {description && <p className="mt-0.5 text-[13px] text-fg-2">{description}</p>}
        </div>
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </header>
  );
}
